import React, { useState, useEffect, useContext } from 'react';
import {
  Row,
  Col,
  Stack,
  Form,
  Button,
  InputGroup,
  Modal,
  Table,
} from 'react-bootstrap';
import MapView from './parts/MapView';
import ChartView from './parts/ChartView';
import CardItem from './parts/CardItem';
import { NotifyModalView, NotifyButtonView } from './parts/NotifySettingView';
import DeviceService, {
  DeviceItem,
  DeviceSensor,
} from '#/services/DeviceService';
import { useParams } from 'react-router-dom';
import { SpinnerView } from './parts/SpinnerView';
import dayjs from 'dayjs';
import { useForm, Controller } from 'react-hook-form';
import { UserContext } from './App';
import { useNavigate } from 'react-router-dom';
import { CSVLink } from 'react-csv';

type SensorRange = {
  begin: dayjs.Dayjs;
  end: dayjs.Dayjs;
};

function ChildInfo() {
  const ctx = useContext(UserContext);
  const [device, setDevice] = useState<DeviceItem>();
  const { id } = useParams();
  const [emailSetting, setEmailSetting] = useState<boolean>(false);
  const [smsSetting, setSmsSetting] = useState<boolean>(false);
  const { control, handleSubmit, formState, reset } = useForm({});
  const service = new DeviceService('child', ctx.userInfo!);
  const [voltLimit, setVoltLimit] = useState<number>(0);
  const navigate = useNavigate();
  const [sensorData, setSensorData] = useState<DeviceSensor[] | undefined>();
  const [sensorTime, setSensorTime] = useState<SensorRange>();
  const [rangeBegin, setRangeBegin] = useState<dayjs.Dayjs>();
  const [rangeEnd, setRangeEnd] = useState<dayjs.Dayjs>();
  const [showData, setShowData] = useState<boolean>(false);

  useEffect(() => {
    service.getInfo(id!).then(
      (res) => {
        setDevice(res);
        setVoltLimit(res.volt_limit || 0);

        const end = dayjs().tz().startOf('h');
        const begin = end.subtract(3, 'days').startOf('h');

        setRangeBegin(begin);
        setRangeEnd(end);
        setSensorTime({ begin, end });
      },
      (err) => {
        console.log(err);
        if (err?.response?.status === 401) {
          navigate(0);
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sensorTime || !device) {
      return;
    }

    service.getSensor(device!.device_id, sensorTime.begin, sensorTime.end).then(
      (res) => {
        setSensorData(res);
      },
      (err) => {
        console.error(err);
      }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensorTime]);

  const onVoltLimit = (values: any) => {
    service
      .modifyDevice({
        ...values,
        id,
        owner_id: device!.owner_id,
        device_id: device!.device_id,
      })
      .then((_) => {
        setDevice({
          ...device!,
          volt_limit: Number(values.volt_limit),
        });

        reset();
      });
  };

  const onRangeChange = (evt: any) => {
    const change = dayjs(evt.target.value);

    switch (evt.target.name) {
      case 'from':
        setRangeBegin(change.startOf('h'));
        break;

      case 'to':
        setRangeEnd(change.startOf('h'));
        break;
    }
  };

  const onChangeSensorTime = () => {
    if (!rangeBegin || !rangeEnd) {
      return;
    }

    setSensorTime({
      begin: rangeBegin!,
      end: rangeEnd!,
    });
    setSensorData(undefined);
  };

  if (!device || !sensorTime) {
    return <SpinnerView />;
  }

  const csvHeaders = [
    {
      label: '識別子',
      key: 'identifier',
    },
    { label: '日付', key: 'date' },
    { label: '時刻', key: 'time' },
    { label: '測定電圧[V]', key: 'volt' },
    { label: '検電器電池残量[V]', key: 'battery' },
    { label: '温度', key: 'temperture' },
    { label: '湿度', key: 'humidity' },
    { label: '電波強度', key: 'radio' },
  ];

  return (
    <>
      <header className="page-header page-header-dark bg-gradient-primary-to-secondary pb-10">
        <div className="container-xl px-4">
          <div className="page-header-content pt-1">
            <div className="row align-items-center justify-content-between"></div>
          </div>
        </div>
      </header>
      <div className="container-xl px-4 mt-n10">
        <div className="card mb-4">
          <div className="card-body">
            <Row>
              <Col>
                <Stack direction="horizontal">
                  <span>子機ID</span>
                  <span>：</span>
                  <span>{device.id}</span>
                </Stack>
                <Stack direction="horizontal">
                  <span>親機ID</span>
                  <span>：</span>
                  <span>{device.owner_id}</span>
                </Stack>
                <Stack direction="horizontal">
                  <span>識別ID</span>
                  <span>：</span>
                  <span>{device.device_id}</span>
                </Stack>
                <Stack direction="horizontal">
                  <span>設置日</span>
                  <span>：</span>
                  <span>{dayjs(device.created_at).format('YYYY/MM/DD')}</span>
                </Stack>
                <Stack direction="horizontal">
                  <span>最終更新日</span>
                  <span>：</span>
                  <span>
                    {dayjs(device.updated_at).format('YYYY/MM/DD HH:mm:ss')}
                  </span>
                </Stack>
                <Stack direction="horizontal">
                  <span>最終センサー更新日</span>
                  <span>：</span>
                  <span>
                    {device.sensor?.receive_at
                      ? dayjs(device.sensor?.receive_at).format(
                          'YYYY/MM/DD HH:mm:ss'
                        )
                      : ''}
                  </span>
                </Stack>
              </Col>
              <Col className="text-end" xs="3">
                {device ? (
                  <Button
                    className="w-100"
                    variant="warning"
                    href={`/child/modify/${device.id}`}
                  >
                    編集
                  </Button>
                ) : (
                  <></>
                )}
              </Col>
            </Row>
            <Row className="my-3">
              <Col>
                <InputGroup>
                  <Form.Control
                    type="datetime-local"
                    name="from"
                    step="3600"
                    placeholder="データ開始日"
                    onChange={onRangeChange}
                    value={rangeBegin?.format('YYYY-MM-DDTHH:mm')}
                  />
                  <InputGroup.Text>〜</InputGroup.Text>
                  <Form.Control
                    type="datetime-local"
                    name="to"
                    step="3600"
                    placeholder="データ終了日"
                    onChange={onRangeChange}
                    value={rangeEnd?.format('YYYY-MM-DDTHH:mm')}
                  />
                  <Button
                    variant="warning"
                    onClick={onChangeSensorTime}
                    disabled={!sensorData}
                  >
                    {sensorData ? '更新' : <SpinnerView size="sm" />}
                  </Button>
                </InputGroup>
              </Col>
              <Col xs="auto">
                <Button
                  className="text-end"
                  variant="link"
                  onClick={() => {
                    setShowData(true);
                  }}
                >
                  もっと見る
                </Button>
              </Col>
            </Row>
            <Row className="my-3">
              <Col>
                <CardItem title="電圧">
                  <ChartView
                    title={'管理電圧'}
                    data={sensorData}
                    limit={device.volt_limit || 0}
                    startTime={sensorTime.begin}
                    endTime={sensorTime.end}
                    kind={'volt'}
                  />
                </CardItem>
              </Col>
              <Col>
                <CardItem title="バッテリー">
                  <ChartView
                    title={'乾電池交換目安'}
                    data={sensorData}
                    startTime={sensorTime.begin}
                    endTime={sensorTime.end}
                    limit={2}
                    kind={'battery'}
                  />
                </CardItem>
              </Col>
            </Row>
            <Row className="mt-4 mb-3">
              <Col>
                <CardItem title="閾値の設定">
                  <Form onSubmit={handleSubmit(onVoltLimit)}>
                    <Row>
                      <Col>
                        <Form.Group className="mb-3">
                          <Form.Label>{voltLimit}</Form.Label>
                          <Controller
                            control={control}
                            name="volt_limit"
                            render={({ field: { onChange, value, ref } }) => (
                              <Form.Range
                                onChange={(elm) => {
                                  onChange(elm);
                                  setVoltLimit(Number(elm.target.value));
                                }}
                                value={voltLimit || 0}
                                ref={ref}
                                min={0}
                                max={15000}
                                step={500}
                              />
                            )}
                          />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group className="mb-3 text-center">
                          <Controller
                            control={control}
                            name="volt_limit"
                            render={({ field: { ref } }) => (
                              <Button
                                type="submit"
                                disabled={formState.isSubmitted}
                              >
                                {formState.isSubmitted ? (
                                  <SpinnerView />
                                ) : (
                                  '閾値を更新'
                                )}
                              </Button>
                            )}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Form>
                </CardItem>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <CardItem title="設置位置">
                  <div style={{ height: '300px' }}>
                    <MapView {...device.location} />
                  </div>
                </CardItem>
              </Col>
            </Row>
            <Row className="mt-4">
              <Col>
                <CardItem title="お知らせ設定">
                  <NotifyButtonView
                    title="メール"
                    verifierd={device.notify.email_verifier}
                    onEnable={() => setEmailSetting(true)}
                    onDisable={() => {
                      setEmailSetting(true);
                    }}
                  />
                  <NotifyButtonView
                    title="ＳＭＳ"
                    verifierd={device.notify.sms_verifier}
                    onEnable={() => setSmsSetting(true)}
                    onDisable={() => {
                      setSmsSetting(true);
                    }}
                  />
                </CardItem>
              </Col>
            </Row>
          </div>
        </div>
      </div>
      <NotifyModalView
        notify_id={String(device.id)}
        notify_type={device.type}
        notify_kind="email"
        title="メールアドレス"
        isShow={emailSetting}
        isVerify={device.notify.email_verifier}
        onClose={() => {
          setEmailSetting(false);
        }}
        onSubmit={(value) => {
          setDevice({
            ...device,
            notify: {
              ...device.notify,
              ...value,
            },
          });
        }}
      />
      <NotifyModalView
        notify_id={String(device.id)}
        notify_type={device.type}
        notify_kind="sms"
        title="電話番号"
        isShow={smsSetting}
        isVerify={device.notify.sms_verifier}
        onClose={() => {
          setSmsSetting(false);
        }}
        onSubmit={(value) => {
          setDevice({
            ...device,
            notify: {
              ...device.notify,
              ...value,
            },
          });
        }}
      />

      <Modal
        show={showData}
        size="lg"
        onHide={() => {
          setShowData(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>データ一覧</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table>
            <thead>
              <tr>
                <th>時</th>
                <th>測定電圧</th>
                <th>検電器電池残量</th>
                <th>出力電圧</th>
                <th>温度</th>
                <th>湿度</th>
                <th>電波強度</th>
              </tr>
            </thead>
            <tbody>
              {sensorData && sensorData.length > 0 ? (
                sensorData.map((sensor) => {
                  return (
                    <tr
                      key={`${sensor.identifier}_${dayjs(
                        sensor.receive_at
                      ).valueOf()}`}
                    >
                      <td>{sensor.receive_at}</td>
                      <td>{sensor.volt}V</td>
                      <td>{sensor.battery}V</td>
                      <td>{sensor.volt > voltLimit ? '正常' : '異常'}</td>
                      <td>{sensor.temperture}</td>
                      <td>{sensor.humidity}</td>
                      <td>{sensor.radio}</td>
                    </tr>
                  );
                })
              ) : (
                <></>
              )}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          {sensorData && sensorData.length > 0 ? (
            <>
              <CSVLink
                className="btn btn-primary"
                headers={csvHeaders}
                data={sensorData?.map((d) => ({
                  ...d,
                  date: dayjs(d.receive_at).format('YYYY/MM/DD'),
                  time: dayjs(d.receive_at).format('HH:mm:ss'),
                }))}
                filename={`fence_${device?.device_id}_${dayjs().format(
                  'YYYYMMDD_HHmmss'
                )}`}
                target="_blank"
              >
                ダウンロード
              </CSVLink>
            </>
          ) : (
            <></>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ChildInfo;
